import React, { useCallback, useMemo } from "react"

import { Chip } from "@mui/material"
import { useToggle } from "react-use"

import { slugify } from "@l2r-front/l2r-utils"

import * as Styled from "./ChipList.styled"


interface ChipValue {
    color?: string;
    key?: string;
    icon?: React.ReactNode;
    label: string;
    secondaryColor?: string;
    value: string | number;
}

type ChipListProps = {
    className?: string,
    onDelete?: ((_chip: ChipValue) => void) | false,
    onAdd?: (_value: string) => void,
    options?: string[],
    values: ChipValue[],
    maxDisplayedChips?: number,
    forceDisplayAddButton?: boolean,
    unfoldable?: boolean,
    small?: boolean,
} & React.ComponentProps<typeof Chip>

export function ChipList(props: ChipListProps): React.ReactNode {

    const {
        className,
        color = "primary",
        onAdd,
        onDelete,
        options,
        values,
        variant = "outlined",
        maxDisplayedChips = -1,
        forceDisplayAddButton = false,
        unfoldable = false,
        small = false,
        ...chipsProps
    } = props

    const [isFolded, toggleIsFolded] = useToggle(true)

    const handleRemoveChip = useCallback((chip: ChipValue) => {
        const chipIndex = values.findIndex((chipFromList: ChipValue) => {
            return chipFromList.value === chip.value
        })

        if (onDelete && chipIndex >= 0) {
            return onDelete(chip)
        }
    }, [onDelete, values])

    const handleFoldingChipClick = useCallback(() => {
        toggleIsFolded()
    }, [toggleIsFolded])

    const chipsToDisplay = useMemo(() => {
        const chips = values.map((chip, index) => {
            const name = typeof chip.value === "string" ? slugify(chip.value) : slugify(chip.label)
            const currentColor = color === "secondary" ? chip.secondaryColor || "secondary" : chip.color || color

            return <Styled.Chip
                color={currentColor}
                key={`chip-${index}`}
                label={chip.label}
                id={`active-filter-${name}`}
                icon={chip.icon}
                {...chipsProps}
                onDelete={onDelete ? () => handleRemoveChip(chip) : null}
                size={small ? "small" : "medium"}
                textColor={color === "secondary" ? chip.color : undefined}
                variant={variant}
            />
        })

        let foldingChip = null
        if (maxDisplayedChips > 0 && chips.length > maxDisplayedChips) {
            if (isFolded) {
                const leftOverChipsCount = chips.splice(maxDisplayedChips).length
                if (leftOverChipsCount > 0) {
                    foldingChip = (
                        <Styled.Chip
                            color={color}
                            label={`+${leftOverChipsCount}`}
                            clickable={unfoldable}
                            onClick={handleFoldingChipClick}
                            size={small ? "small" : "medium"}
                        />
                    )
                }
            } else {
                foldingChip = (
                    <Styled.Chip
                        color={color}
                        label={"<"}
                        clickable
                        onClick={handleFoldingChipClick}
                        size={small ? "small" : "medium"}
                    />
                )
            }
        }

        return (
            <Styled.ChipsWrapper className={className}>
                {chips}
                {foldingChip}
                {(!!onAdd || forceDisplayAddButton) && <Styled.ChipAdd
                    chipList={values.map(v => v.value.toString())}
                    color={color}
                    id="add-chip"
                    options={options}
                    onValueSelected={onAdd}
                    small={small}
                    variant={variant}
                    values={values.map(v => ({
                        label: v.label,
                        value: v.value.toString()
                    }))}
                    disabled={!onAdd && forceDisplayAddButton}
                />}
            </Styled.ChipsWrapper>
        )
    }, [className,
        color,
        values,
        isFolded,
        maxDisplayedChips,
        unfoldable,
        chipsProps,
        handleRemoveChip,
        handleFoldingChipClick,
        onAdd,
        options,
        onDelete,
        small,
        variant,
        forceDisplayAddButton])

    return chipsToDisplay
}
